import React, { useMemo } from 'react';
import { Box, Stack, useTheme } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker as MUIDatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { addDays, isBefore, parseISO, format } from 'date-fns';
import { DashboardDateRange } from '../../types';

interface DatePickerProps extends DashboardDateRange {
  onChange: (range: DashboardDateRange) => void;
  className?: string;
}

const DatePicker: React.FC<DatePickerProps> = ({ startDate, endDate, onChange, className }) => {
  const theme = useTheme();

  const parsedRange = useMemo(() => {
    const parse = (value: string) => parseDate(value);
    return {
      start: parse(startDate),
      end: parse(endDate),
    };
  }, [startDate, endDate]);

  const handleStartChange = (value: Date | null) => {
    const nextStart = formatDate(value);
    let nextEnd = endDate;

    if (value && parsedRange.end && !isBefore(value, parsedRange.end)) {
      const adjustedEnd = addDays(value, 1);
      nextEnd = formatDate(adjustedEnd);
    }

    onChange({ startDate: nextStart, endDate: nextEnd });
  };

  const handleEndChange = (value: Date | null) => {
    const nextEnd = formatDate(value);
    let nextStart = startDate;

    if (value && parsedRange.start && !isBefore(parsedRange.start, value)) {
      const adjustedStart = addDays(value, -1);
      nextStart = formatDate(adjustedStart);
    }

    onChange({ startDate: nextStart, endDate: nextEnd });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        className={className}
        sx={{
          p: 2,
          borderRadius: 2,
          boxShadow: 'inset 0 0 0 1px rgba(15, 23, 42, 0.05)',
          backgroundColor: theme.palette.common.white,
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <MUIDatePicker
            label="Start date"
            value={parsedRange.start}
            onChange={handleStartChange}
            maxDate={parsedRange.end ? addDays(parsedRange.end, -1) : undefined}
            format="dd/MM/yyyy"
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
          <MUIDatePicker
            label="End date"
            value={parsedRange.end}
            onChange={handleEndChange}
            minDate={parsedRange.start ? addDays(parsedRange.start, 1) : undefined}
            format="dd/MM/yyyy"
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Stack>
      </Box>
    </LocalizationProvider>
  );
};

const parseDate = (value: string) => {
  if (!value) return null;
  try {
    const parsed = parseISO(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch (error) {
    return null;
  }
};

const formatDate = (value: Date | null) => (value ? format(value, 'yyyy-MM-dd') : '');

export default DatePicker;
